import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Divider, Grid } from 'semantic-ui-react'
import {
  faUpload,
  faTrashCan,
  faSquareUpRight,
} from '@fortawesome/pro-regular-svg-icons'

import {
  uploadDocument,
  UploadUserDocumentPayload,
  userUploadDocument,
  UploadError,
} from '../../actions/awsActions'
import { documentCategoryOptions } from '@/features/admin/documents/helpers'
import {
  getUserDocumentCategoriesById,
  selectUserDocumentCategory,
  selectUserDocumentCategoryByInternalName,
} from '@/features/admin/user-document-categories/userDocumentCategories.selectors'
import { User } from '../../reducers/auth/userReducer'
import {
  Button,
  Text,
  Alert,
  Dropdown,
  Modal,
  GridRowColumn,
  Card,
  IconButton,
  Icon,
  Checkbox,
  Link,
  DatePicker,
  Popup,
} from '../BaseComponents'
import { UserDocument } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { TaxFilingUser } from '@/features/taxes/annual-taxes/annualTaxFilings.slice'
import { getCurrentUser } from '../../selectors/user.selectors'
import { useReselector } from '../../utils/sharedHooks'
import FileUploadModalDescription from './FileUploadModalDescription'
import { fetchFinancialAccountsIfNeeded } from '../../actions/financialAccountActions'
import { fetchUserFinancialAccounts } from '../../actions/admin/adminFinancialAccountActions'
import { UploadDocumentType } from '../../constants/businessConstants'
import { parseErrorFromCatch } from '../../utils/errorHelpers'
import { getBookkeepingTitle } from './BookkeepingDescription'
import { DescriptionType } from '@/features/user/your-practice/documents/lib/constants'
import { useAnalyticsTrack } from '@/features/amplitude'
import { DATE_FORMATS_LUXON } from '../../utils/dateHelpers'
import { StatementUploadSection, StatementData } from './StatementUploadSection'
import { DateTime } from 'luxon'
import {
  fetchAdminUserDocumentCategoriesIfNeeded,
  fetchUserDocumentCategoriesIfNeeded,
} from '@/features/admin/user-document-categories/userDocumentCategories.slice'
import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { useAppDispatch } from '../../utils/typeHelpers'
import { isAdminContext } from '../../utils/adminContext'
import { selectOnboardingStepCompletedOrSkipped } from '@/features/onboarding/user-onboarding-steps/onboarding.selectors'
import { GettingStartedOnboardingStep } from '@/features/onboarding/user-onboarding-steps/onboarding.reducer'
import { fetchUserOnboardingStepsIfNeeded } from '@/features/onboarding/user-onboarding-steps/onboarding.actions'
import { ViewMissingStatementsAccordionForCatchupBookkeeping } from '@/features/user/dashboard/onboarding/getting-started-items/OnboardingItemCTAContent'
import { MissingStatements } from '@/features/user/your-practice/documents/shared/MissingStatements'
import { UserDocumentCategoryIdentifier } from '@/features/admin/user-document-categories/userDocumentCategory.constants'

export interface UploadedFile {
  title: string
  url: string
  id: number
  statementMonth?: DateTime
  taxDocumentType?: 'state' | 'federal'
  fileName?: string
  doc?: UserDocument
}

interface DocumentTypes {
  value: UploadDocumentType
  text: string
}

const transformMonthYear = (monthYear?: string) => {
  return monthYear
    ? DateTime.fromFormat(monthYear, DATE_FORMATS_LUXON.YEAR_MONTH, {
        zone: 'utc',
      })
    : undefined
}

const formatMonthYear = (monthYear?: string) => {
  return monthYear
    ? DateTime.fromFormat(
        monthYear,
        DATE_FORMATS_LUXON.YEAR_MONTH_LOOSE
      ).toFormat(DATE_FORMATS_LUXON.YEAR_MONTH)
    : undefined
}

interface Props {
  open: boolean
  close: () => void
  documentType: UploadDocumentType
  userFacing: boolean
  user?: User | TaxFilingUser
  categoryId?: number | null
  categoryInternalName?: string
  setUploadedFile?: (_: UploadedFile) => Promise<void> | void
  setUploadedFiles?: (_: UploadedFile[]) => Promise<void> | void
  title?: string
  taxFilingFormId?: number
  description?: ReactNode
  monthYear?: string // YYYY-MM format
  financialAccountId?: number[]
  subHeaderText?: string
  // Sometimes a file should be uploaded with extra props
  extraPayload?: Partial<UploadUserDocumentPayload>

  // Handles checkbox at bottom of modal that can be used to skip uploading
  overrideCheckboxLabel?: ReactNode
  overrideCallback?: () => void

  // The header text displayed at the top of the modal
  modalHeaderText?: string
  hideModalHeaderText?: boolean

  descriptionType?: DescriptionType | null
  year?: string
  useFilteredAccountOptions?: boolean
  isUploadOnly?: boolean
  taxYearDisabled?: boolean
  isMixedType?: boolean
  internalCategoryNameFlag?: string
  documentCategoryMap?: Map<
    UploadDocumentType,
    UserDocumentCategoryIdentifier[]
  >
  requiredDocumentCount?: number
  uploadedDocumentCount?: number
}

const FileUploadModal = ({
  open,
  close,
  userFacing,
  documentType: sentDocumentType,
  setUploadedFile,
  setUploadedFiles,
  taxFilingFormId,
  extraPayload,
  user: sentUser,
  title: sentTitle,
  description: sentDescription,
  categoryId: sentCategoryId,
  categoryInternalName: sentCategoryInternalName,
  monthYear: sentMonthYear,
  financialAccountId: sentFinancialAccountId,
  subHeaderText: sentSubHeaderText,
  overrideCheckboxLabel,
  overrideCallback,
  modalHeaderText,
  hideModalHeaderText,
  descriptionType,
  year: sentYear,
  useFilteredAccountOptions = false,
  isUploadOnly = false,
  taxYearDisabled = false,
  isMixedType = false,
  internalCategoryNameFlag = '',
  documentCategoryMap,
  requiredDocumentCount,
  uploadedDocumentCount = 0,
}: Props) => {
  const dispatch = useAppDispatch()
  const [documentTypes] = useState<DocumentTypes[]>([
    { value: UploadDocumentType.BOOKKEEPING, text: 'Bookkeeping Documents' },
    { value: UploadDocumentType.TAX, text: 'Tax Documents' },
  ])
  const [documentType, setDocumentType] = useState(sentDocumentType)
  const [filesToUpload, setFilesToUpload] = useState<File[]>([])
  const [error, setError] = useState('')
  const [uploading, setUploading] = useState(false)
  const [statementData, setStatementData] = useState<StatementData[]>([])
  const [month, setMonth] = useState(() => {
    const parsed = transformMonthYear(sentMonthYear)
    return parsed?.month ? parsed.month - 1 : undefined
  })
  const [internalCategoryNames, setInternalCategoryNames] = useState<string[]>(
    []
  )
  const [year, setYear] = useState(
    sentYear ?? transformMonthYear(sentMonthYear)?.year?.toString()
  )
  const [userDocumentCategoryId, setUserDocumentCategoryId] = useState(
    sentCategoryId ?? undefined
  )
  const currentTaxYear = useReselector(selectCurrentAnnualTaxYear)
  const nextTaxYear = (Number(currentTaxYear) + 1).toString()
  const [taxDocumentType, setTaxDocumentType] = useState<'federal' | 'state'>()
  const [uploadOverride, setUploadOverride] = useState(false)
  const [hideDocumentCategoryMixed, setHideDocumentCategoryMixed] =
    useState(false)
  const documentCategories = useReselector(getUserDocumentCategoriesById)
  const filteredDocumentCategories = useMemo(
    () =>
      documentCategoryOptions(
        documentCategories,
        documentType,
        false,
        internalCategoryNames
      ),
    [documentCategories, documentType, internalCategoryNames]
  )
  const sentCategoryById = useReselector(
    selectUserDocumentCategory,
    sentCategoryId
  )
  const sentCategoryByName = useReselector(
    selectUserDocumentCategoryByInternalName,
    sentCategoryInternalName
  )
  const sentCategoryByInternalName = useReselector(
    selectUserDocumentCategoryByInternalName,
    internalCategoryNameFlag
  )
  useEffect(() => {
    if (isMixedType) {
      if (documentCategoryMap) {
        const filteredInternalNames = documentCategoryMap.get(documentType)
        if ((filteredInternalNames?.length ?? 0) > 0) {
          setInternalCategoryNames(filteredInternalNames ?? [])
          setUserDocumentCategoryId(undefined)
          setHideDocumentCategoryMixed(false)
        } else {
          setUserDocumentCategoryId(sentCategoryByInternalName?.id ?? undefined)
          setHideDocumentCategoryMixed(true)
        }
      }
    }
  }, [
    documentCategoryMap,
    documentType,
    isMixedType,
    sentCategoryByInternalName?.id,
  ])

  const sentCategory = sentCategoryById || sentCategoryByName
  const currentUser = useReselector(getCurrentUser)
  const inputRef = useRef<HTMLInputElement>(null)
  const track = useAnalyticsTrack()

  const user = sentUser || currentUser

  const isAnnualCategory = useMemo(() => {
    return Object.values(documentCategories).find(
      (dc) => dc.id === userDocumentCategoryId
    )?.isAnnual
  }, [userDocumentCategoryId, documentCategories])

  const uploadBankStatementOnboardingStepIsDone = useReselector(
    selectOnboardingStepCompletedOrSkipped,
    GettingStartedOnboardingStep.uploadBankStatements
  )

  const shouldRenderCatchupBKDescription =
    !uploadBankStatementOnboardingStepIsDone &&
    documentType === UploadDocumentType.STATEMENT

  const shouldRenderMissingStatements =
    documentType === UploadDocumentType.STATEMENT

  useEffect(() => {
    setDocumentType(sentDocumentType)
  }, [sentDocumentType])
  // If the category id prop changes update the local state
  useEffect(() => {
    setUserDocumentCategoryId(sentCategory?.id ?? undefined)
  }, [sentCategory?.id])

  // If a sent month is sent update year and month local state
  useEffect(() => {
    if (!sentYear) {
      setYear(transformMonthYear(sentMonthYear)?.year?.toString())
      const parsed = transformMonthYear(sentMonthYear)
      setMonth(parsed?.month ? parsed.month - 1 : undefined)
    }
  }, [sentMonthYear, sentYear])

  // if Bank Statement modal fetch financial account information when needed
  useEffect(() => {
    if (documentType === UploadDocumentType.STATEMENT) {
      if (userFacing) {
        dispatch(fetchFinancialAccountsIfNeeded())
      } else if (user) {
        dispatch(fetchUserFinancialAccounts(user.id, true))
      }
    }
  }, [documentType, userFacing, user, dispatch])

  useEffect(() => {
    dispatch(
      isAdminContext()
        ? fetchAdminUserDocumentCategoriesIfNeeded()
        : fetchUserDocumentCategoriesIfNeeded()
    )
    // Onboarding steps drive customer-facing upload copy only; skip in the admin
    // context, where the customer endpoint is unauthenticated (Stytch admin).
    if (!isAdminContext()) {
      dispatch(
        fetchUserOnboardingStepsIfNeeded({
          expanded: true,
        })
      )
    }
  }, [dispatch])

  const uploadFiles = useCallback(async () => {
    if (!user?.id) {
      return
    }

    const uploadFile = async (
      file: File,
      index: number
    ): Promise<UploadedFile | null> => {
      let docRes: UserDocument | UploadError | null = null

      // Use individual file's month selection from statementData, not global month/year
      let statementMonth: DateTime | undefined
      if (statementData[index]?.statementMonth) {
        const parsed = DateTime.fromFormat(
          statementData[index].statementMonth,
          DATE_FORMATS_LUXON.YEAR_MONTH,
          { zone: 'utc' }
        )
        if (parsed.isValid) {
          statementMonth = parsed
        }
      } else if (year !== undefined && month !== undefined) {
        // Fallback to global month/year if no individual selection
        statementMonth = DateTime.fromObject(
          { year: Number(year), month: month + 1 },
          { zone: 'utc' }
        )
      }

      const payload = {
        file,
        user: {
          id: user.id,
          uuid: user.uuid,
        },
        documentType,
        statementMonth,
        userDocumentCategoryId,
        year:
          documentType === UploadDocumentType.TAX && year ? year : undefined,
        ...extraPayload,
      }

      if (statementData[index]) {
        const statement = {
          statementMonth: statementData[index].statementMonth,
          financialAccountIds: statementData[index].financialAccountIds,
        }
        payload.statement = statement
      }

      if (userFacing) {
        const res = await userUploadDocument(payload)(dispatch)
        if (res && 'userDocument' in res) {
          docRes = res.userDocument
        } else if (res && 'error' in res) {
          docRes = res
        }
      } else {
        docRes = await uploadDocument(payload)(dispatch)
      }

      if (docRes && 'error' in docRes) {
        throw new Error(docRes.error)
      }

      if (docRes && 'id' in docRes) {
        return {
          title: docRes.fileDescription || '',
          url: docRes.signedUrl || '',
          id: docRes.id,
          statementMonth,
          taxDocumentType,
          fileName: file.name,
          doc: docRes,
        }
      }
      return null
    }

    setError('')
    setUploading(true)
    try {
      const results = await Promise.all(
        filesToUpload.map((file, index) => uploadFile(file, index))
      )
      const successfulUploads = results.filter(
        (r): r is UploadedFile => r !== null
      )

      const uploadedFiles: UploadedFile[] = successfulUploads.map((result) => ({
        ...result,
      }))

      if (setUploadedFiles) {
        await setUploadedFiles(uploadedFiles)
      } else if (setUploadedFile && uploadedFiles.length > 0) {
        await setUploadedFile(uploadedFiles[0])
      }

      setFilesToUpload([])
      close()
    } catch (err) {
      setError(parseErrorFromCatch(err))
    } finally {
      setUploading(false)
    }
  }, [
    user?.id,
    user?.uuid,
    year,
    month,
    documentType,
    userDocumentCategoryId,
    extraPayload,
    userFacing,
    setUploadedFile,
    setUploadedFiles,
    dispatch,
    filesToUpload,
    close,
    taxDocumentType,
    statementData,
  ])

  const handleRemove = useCallback(
    (index: number) =>
      setFilesToUpload([
        ...filesToUpload.slice(0, index),
        ...filesToUpload.slice(index + 1),
      ]),
    [filesToUpload]
  )

  const title = useMemo(() => {
    if (sentTitle) {
      return sentTitle
    } else if (
      documentType === UploadDocumentType.BOOKKEEPING &&
      sentCategory
    ) {
      return getBookkeepingTitle(sentCategory, descriptionType)
    } else if (sentCategory?.title) {
      return sentCategory.title
    } else if (documentType === UploadDocumentType.TAX) {
      return 'Tax Documents'
    } else if (documentType === UploadDocumentType.INCORPORATION) {
      return 'Incorporation Documents'
    } else if (documentType === UploadDocumentType.OTHER) {
      return 'Other Documents'
    } else if (documentType === UploadDocumentType.RECEIPT) {
      return 'Receipts'
    } else if (documentType === UploadDocumentType.BOOKKEEPING) {
      return 'Bookkeeping Documents'
    }
    return 'Bank Statements'
  }, [sentCategory, documentType, sentTitle, descriptionType])

  const onUploadButtonClicked = useCallback(() => {
    const trackProperties: {
      document_category: string
      document_name: string
      document_subcategory: string
      report_month?: number
      report_year?: number
      tax_year?: number
    } = {
      document_category: documentType,
      document_subcategory: sentCategory?.id.toString() || '',
      document_name: sentCategory?.title || '',
    }

    if (year) {
      if (documentType === UploadDocumentType.STATEMENT) {
        trackProperties.report_year = Number(year)
      } else {
        trackProperties.tax_year = Number(year)
      }
    }
    if (month) {
      trackProperties.report_month = month
    }

    track('clicked document upload', trackProperties)

    if (uploadOverride) {
      overrideCallback?.()
      close()
    } else {
      uploadFiles()
    }
  }, [
    close,
    overrideCallback,
    uploadFiles,
    uploadOverride,
    track,
    sentCategory,
    documentType,
    month,
    year,
  ])

  const isUploadDisabled = useMemo(() => {
    // If upload override checkbox checked then enabled
    if (uploadOverride) {
      return false
    }

    // If no files are uploaded then disabled
    if (filesToUpload.length === 0) {
      return true
    }

    if (
      documentType === UploadDocumentType.STATEMENT &&
      !statementData?.length
    ) {
      return true
    }
    return (
      documentType === UploadDocumentType.STATEMENT &&
      !statementData.every(
        (item) => item.statementMonth && item.financialAccountIds?.length
      )
    )
  }, [documentType, filesToUpload.length, uploadOverride, statementData])

  return (
    <Modal size="small" dimmer="inverted" open={open} onClose={close} closeIcon>
      <Modal.Header>
        {!hideModalHeaderText && (modalHeaderText || 'Upload documents')}
      </Modal.Header>
      <Modal.Content>
        <Card backgroundColor="stone">
          <Grid>
            {error && (
              <GridRowColumn>
                <Alert type="error" title={error} />
              </GridRowColumn>
            )}
            <GridRowColumn>
              <Text as="h2">{title}</Text>
              <Text>
                {sentSubHeaderText ||
                  `Uploaded ${uploadedDocumentCount} of ${requiredDocumentCount || ' '}
                documents`}
              </Text>
            </GridRowColumn>
            <GridRowColumn>
              <FileUploadModalDescription
                descriptionType={descriptionType}
                sentDescription={sentDescription}
                documentType={documentType}
                sentCategoryId={sentCategory?.id}
                sentMonthYear={sentMonthYear}
                sentYear={sentYear?.toString()}
                forCatchUpBK={shouldRenderCatchupBKDescription}
                missingStatements={shouldRenderMissingStatements}
              />
            </GridRowColumn>
            {shouldRenderCatchupBKDescription ? (
              <GridRowColumn>
                <ViewMissingStatementsAccordionForCatchupBookkeeping />
              </GridRowColumn>
            ) : shouldRenderMissingStatements ? (
              <GridRowColumn>
                <MissingStatements includeUploadButton={false} accordionOnly />
              </GridRowColumn>
            ) : null}

            <GridRowColumn computer={7} mobile={16}>
              <Button
                fullWidth
                onClick={() => inputRef.current?.click()}
                disabled={Boolean(
                  requiredDocumentCount &&
                  uploadedDocumentCount >= requiredDocumentCount
                )}
              >
                <Icon icon={faUpload} style={{ marginRight: 10 }} />
                Select Documents
              </Button>
              <input
                type="file"
                multiple
                hidden
                onChange={(e) => {
                  if (e.target.files) {
                    const filesArray = Array.from(e.target.files)
                    const remainingRequired = requiredDocumentCount
                      ? requiredDocumentCount -
                        uploadedDocumentCount -
                        filesToUpload.length
                      : undefined
                    const filesToAdd =
                      remainingRequired !== undefined
                        ? filesArray.slice(0, remainingRequired)
                        : filesArray

                    if (
                      remainingRequired !== undefined &&
                      filesArray.length > remainingRequired
                    ) {
                      setError(
                        `Only ${remainingRequired} more file${remainingRequired === 1 ? '' : 's'} needed. Additional files were not added.`
                      )
                    }

                    setFilesToUpload((prev) => [...prev, ...filesToAdd])
                    if (documentType === UploadDocumentType.STATEMENT) {
                      const defaultStatementData = filesToAdd.map(() => ({
                        statementMonth: formatMonthYear(sentMonthYear) || '',
                        financialAccountIds: sentFinancialAccountId || [],
                      }))
                      setStatementData((prev) => [
                        ...prev,
                        ...defaultStatementData,
                      ])
                    }
                    setUploadOverride(false)
                    e.target.value = ''
                  }
                }}
                ref={inputRef}
              />
            </GridRowColumn>
            {documentType !== UploadDocumentType.STATEMENT &&
              Boolean(filesToUpload.length) && (
                <>
                  <Divider />
                  <GridRowColumn>
                    <Text as="eyebrow">Files to Upload</Text>
                    <ul>
                      {filesToUpload.map((file, index) => (
                        <li key={file.name}>
                          <Text>
                            {file.name}
                            <IconButton
                              onClick={() => handleRemove(index)}
                              icon={faTrashCan}
                              style={{ marginLeft: 6 }}
                            />
                          </Text>
                        </li>
                      ))}
                    </ul>
                  </GridRowColumn>
                </>
              )}
            {Boolean(filesToUpload.length) &&
              documentType === UploadDocumentType.STATEMENT && (
                <StatementUploadSection
                  filesToUpload={filesToUpload}
                  setFilesToUpload={setFilesToUpload}
                  statementData={statementData}
                  setStatementData={setStatementData}
                  user={user}
                  useFilteredAccountOptions={useFilteredAccountOptions}
                />
              )}
            {isMixedType && (
              <>
                <Divider />
                <GridRowColumn>
                  <Dropdown
                    search
                    fullWidth
                    label="Select Document Type"
                    placeholder="Select Document Type"
                    value={documentType}
                    options={documentTypes}
                    onChange={(value) =>
                      setDocumentType(value as UploadDocumentType)
                    }
                    required
                    clearable
                  />
                </GridRowColumn>
              </>
            )}
            {[
              UploadDocumentType.TAX,
              UploadDocumentType.INCORPORATION,
              UploadDocumentType.BOOKKEEPING,
            ].includes(documentType) &&
              !sentCategory?.id &&
              !isUploadOnly &&
              !hideDocumentCategoryMixed && (
                <>
                  <Divider />
                  <GridRowColumn>
                    <Dropdown
                      search
                      fullWidth
                      label="Select Document Category"
                      placeholder="Select Document Category"
                      value={userDocumentCategoryId}
                      options={filteredDocumentCategories}
                      onChange={setUserDocumentCategoryId}
                      required
                      clearable
                    />
                  </GridRowColumn>
                </>
              )}
            {(isAnnualCategory ||
              (documentType === UploadDocumentType.TAX &&
                !userDocumentCategoryId)) &&
              !isUploadOnly && (
                <GridRowColumn>
                  <DatePicker
                    placeholder="Select year"
                    label="Tax Year"
                    value={year?.toString() ?? ''}
                    maxDate={new Date()}
                    onChange={setYear}
                    dateFormat={DATE_FORMATS_LUXON.YEAR_FOUR_DIGIT}
                    showYearPicker
                    clearable
                    disabled={taxYearDisabled}
                    afterLabel={
                      <Popup
                        content={
                          <Grid>
                            <GridRowColumn>
                              <Text>
                                A tax year is the 12-month calendar year covered
                                by a tax return. In the U.S., the tax year for
                                individuals runs from Jan. 1 to Dec. 31 and
                                includes taxes owed on earnings during that
                                period. For example, taxes withheld or owed for
                                earnings during the calendar year{' '}
                                {currentTaxYear} would be included on the tax
                                return sent to the IRS in {nextTaxYear}. Even
                                though you are sending the tax return in{' '}
                                {nextTaxYear}, it is for the tax year{' '}
                                {currentTaxYear}.
                              </Text>
                            </GridRowColumn>
                          </Grid>
                        }
                      />
                    }
                  />
                </GridRowColumn>
              )}
            {taxFilingFormId && (
              <GridRowColumn>
                <Dropdown
                  fluid
                  required
                  label="Select State or Federal"
                  placeholder="Select State or Federal"
                  options={[
                    { text: 'State', value: 'state' },
                    { text: 'Federal', value: 'federal' },
                  ]}
                  value={taxDocumentType}
                  onChange={setTaxDocumentType}
                />
              </GridRowColumn>
            )}
            {(sentCategory?.downloadFormUrl ||
              sentCategory?.viewExampleUrl) && (
              <Grid.Row>
                {sentCategory.downloadFormUrl && (
                  <Grid.Column width={5}>
                    <Link href={sentCategory.downloadFormUrl} newPage>
                      Download form
                      <Icon icon={faSquareUpRight} style={{ marginLeft: 6 }} />
                    </Link>
                  </Grid.Column>
                )}
                {sentCategory.viewExampleUrl && (
                  <Grid.Column width={5}>
                    <Link href={sentCategory.viewExampleUrl} newPage>
                      View Example
                      <Icon icon={faSquareUpRight} style={{ marginLeft: 6 }} />
                    </Link>
                  </Grid.Column>
                )}
              </Grid.Row>
            )}
          </Grid>
        </Card>
        {overrideCheckboxLabel && !filesToUpload.length && (
          <Checkbox
            label={overrideCheckboxLabel}
            onChange={(checked) => setUploadOverride(checked)}
            checked={uploadOverride}
          />
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button variant="secondary" onClick={close}>
          Cancel
        </Button>
        <Button
          onClick={onUploadButtonClicked}
          loading={uploading}
          disabled={Boolean(
            isUploadDisabled ||
            uploading ||
            (requiredDocumentCount &&
              uploadedDocumentCount >= requiredDocumentCount)
          )}
        >
          {uploadOverride
            ? 'Submit'
            : `Upload${filesToUpload.length > 0 ? ` (${filesToUpload.length})` : ''}`}
        </Button>
      </Modal.Actions>
    </Modal>
  )
}

const FileUploadModalWrapper = (props: Props) => {
  if (!props.open) {
    return null
  }

  return <FileUploadModal {...props} />
}

export default FileUploadModalWrapper
